import { FC, ReactNode, useMemo, useState } from 'react'
import { BudgetItemChild } from '../../entities/Budget'
import { colors } from '../../shared/ui/colors'
import {
  TableContainer,
  StyledTable,
  TableHeader,
  HeaderCell,
  BodyRow,
  TitleCell,
  MetricsCell,
  ToggleButton,
  ChevronIcon,
  LeafPlaceholder,
  Indent,
  ItemCode,
  ItemName,
  EstimatesLink
} from './BudgetTable.styles'
import { Icon } from '../../shared/ui/Icon'
import { numberWithSpaces } from '../../shared/utils/formats.ts'
import { routes } from '../../shared/constants/routes'

interface BudgetTableProps {
  items: BudgetItemChild[]
  livingComplexId: string
  objectName?: string | null
}

const getItemKey = (item: BudgetItemChild) => {
  const shouldUseContractOnly =
    !item.costItemCode && !item.costItem && item.contract

  if (shouldUseContractOnly) {
    return item.contract
  }

  return (
    item.costItemStickerId ??
    item.contract ??
    `${item.costItemCode}–${item.costItem}`
  )
}

const getInitialExpandedIds = (
  items: BudgetItemChild[],
  depth = 0,
  maxDepth = 1,
  path: number[] = []
): string[] => {
  return items.reduce<string[]>((acc, item, index) => {
    const currentPath = [...path, index]
    const itemKey = getItemKey(item) ?? currentPath.join('-')
    if (item.children && item.children.length > 0 && depth <= maxDepth) {
      acc.push(itemKey)
      acc.push(
        ...getInitialExpandedIds(
          item.children,
          depth + 1,
          maxDepth,
          currentPath
        )
      )
    }
    return acc
  }, [])
}

// Deterministic palette per nesting level; deeper levels get a muted blue shade
const LEVEL_ACCENTS = ['#FEFCE8', '#F0FDF4', '#ECFEFF', '#FAF5FF'] as const
const EXTRA_LEVEL_BASE_LIGHTNESS = 90
const EXTRA_LEVEL_LIGHTNESS_STEP = 2
const EXTRA_LEVEL_MIN_LIGHTNESS = 78

const getRowAccentByLevel = (level: number) => {
  if (level < LEVEL_ACCENTS.length) {
    return LEVEL_ACCENTS[level]
  }

  const overflowLevel = level - LEVEL_ACCENTS.length + 1
  const lightness =
    EXTRA_LEVEL_BASE_LIGHTNESS - overflowLevel * EXTRA_LEVEL_LIGHTNESS_STEP
  const clampedLightness = Math.max(EXTRA_LEVEL_MIN_LIGHTNESS, lightness)

  return `hsl(210, 55%, ${clampedLightness}%)`
}

export const BudgetTable: FC<BudgetTableProps> = ({
  items,
  livingComplexId,
  objectName
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(getInitialExpandedIds(items))
  )

  const toggleItem = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const rows = useMemo(() => {
    const renderItems = (
      nodes: BudgetItemChild[],
      level = 0,
      path: number[] = []
    ): ReactNode[] => {
      return nodes.flatMap((node, index) => {
        const currentPath = [...path, index]
        const pathKey = currentPath.join('-')
        const itemKey = getItemKey(node) ?? pathKey
        const hasChildren = Boolean(node.children && node.children.length)
        const isExpanded = hasChildren ? expandedIds.has(itemKey) : false
        const accent = getRowAccentByLevel(level)
        const title = node.costItem ?? node.contract ?? '—'
        const estimatesLinkParams = new URLSearchParams()

        if (objectName) {
          estimatesLinkParams.set('oipd', objectName)
        }

        if (node.costItemCode) {
          estimatesLinkParams.set('budget-item', node.costItemCode)
        }

        const estimatesLinkSearch = estimatesLinkParams.toString()
        const estimatesLink = `${routes.costEstimates(
          livingComplexId
        )}${estimatesLinkSearch ? `?${estimatesLinkSearch}` : ''}`

        const row = (
          <BodyRow key={itemKey} accent={accent}>
            <TitleCell level={level}>
              {level > 0 && <Indent level={level} />}
              {hasChildren ? (
                <ToggleButton
                  type="button"
                  onClick={() => toggleItem(itemKey)}
                  aria-expanded={isExpanded}
                  aria-label={
                    isExpanded ? 'Свернуть строку' : 'Развернуть строку'
                  }
                >
                  <ChevronIcon
                    type="chevron_down"
                    size={16}
                    color={colors.gray600}
                    isExpanded={isExpanded}
                  />
                </ToggleButton>
              ) : (
                <LeafPlaceholder />
              )}
              {node.costItemCode && <ItemCode>{node.costItemCode}</ItemCode>}
              <ItemName>{title}</ItemName>
            </TitleCell>
            <MetricsCell>{numberWithSpaces(node.approvedLimit)}</MetricsCell>
            <MetricsCell>{numberWithSpaces(node.kmp)}</MetricsCell>
            <MetricsCell>{numberWithSpaces(node.budget)}</MetricsCell>
            <MetricsCell>{numberWithSpaces(node.contracting)}</MetricsCell>
            <MetricsCell align="center">
              {node.costItemCode && (
                <EstimatesLink to={estimatesLink}>
                  <Icon type="arrow_right" size={16} color={colors.gray600} />
                </EstimatesLink>
              )}
            </MetricsCell>
          </BodyRow>
        )

        if (!hasChildren || !isExpanded) {
          return [row]
        }

        return [
          row,
          ...renderItems(node.children ?? [], level + 1, currentPath)
        ]
      })
    }

    return renderItems(items)
  }, [expandedIds, items, livingComplexId, objectName])

  return (
    <TableContainer>
      <StyledTable>
        <TableHeader>
          <tr>
            <HeaderCell align="left">
              Классификатор статей бюджета/стикер
            </HeaderCell>
            <HeaderCell>Утвержденный лимит с фактической инфляцией</HeaderCell>
            <HeaderCell>КМП</HeaderCell>
            <HeaderCell>Текущий лимит</HeaderCell>
            <HeaderCell>Контрактация</HeaderCell>
            <HeaderCell align="center">Перейти в Сметы</HeaderCell>
          </tr>
        </TableHeader>
        <tbody>{rows}</tbody>
      </StyledTable>
    </TableContainer>
  )
}
